import { CSRF_HEADER, type ProjectView } from "@esi/shared";
import { ChevronDown, FolderClosed, FolderPlus, MessageCircle } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, blockedBySessionEnd } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import {
  AttachmentPicker,
  AttachmentTray,
} from "../conversation/attachments/AttachmentControls.tsx";
import { useAttachmentDraft } from "../conversation/attachments/queries.ts";
import { useAttachmentStart } from "../conversation/attachments/start.ts";
import { ComposerField, ComposerFooter, ComposerFrame } from "../conversation/Composer.tsx";
import { createChat } from "../conversation/chat.ts";
import { useChatCache } from "../conversation/chat-cache.tsx";
import { useRefreshConversations } from "../conversation/queries.ts";
import { useMe } from "../me.ts";
import { EffortPicker, useEffortChoice } from "../models/EffortPicker.tsx";
import { useModelOptions } from "../models/queries.ts";
import { useProjects } from "../projects/queries.ts";
import { conversationPath, navigate, PROJECTS_PATH, projectConversationPath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { holdReload } from "../shell/build-refresh.ts";
import { useUnsavedWork } from "../shell/session-ended.ts";
import { ResidencyNote } from "../trust/ResidencyNote.tsx";
import { handOffQuestion } from "./handoff.ts";
import { lastProject } from "./last-project.ts";

/**
 * Home, the "New chat" screen (spec `web-shell` "Home composer"): one composer whose message
 * starts a standalone conversation (no project, no knowledge) and opens it at `/c/:id`. Its
 * project select lets the user ask inside a project instead. When standalone conversations are
 * off for the user, the select defaults to the last project used and offers no "No project".
 */
export function Home() {
  const { t } = useTranslation();
  const projects = useProjects();
  const me = useMe();

  return (
    <div
      data-esi-content="projects"
      data-scroll-owner="page"
      className="min-h-0 flex-1 overflow-y-auto"
    >
      <div className="flex min-h-full flex-col items-center justify-center px-4 py-10 sm:px-6">
        <div className="flex w-full max-w-2xl flex-col gap-6">
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-balance text-fg sm:text-[28px]">
              {t("home.title")}
            </h1>
            <p className="max-w-md text-sm text-balance text-fg-secondary">{t("home.lead")}</p>
          </div>
          {/* The target defaults from `/api/me` (standalone or a project): nothing is picked,
              typed or dropped until it is known, or it would land in the first project. Gated
              on data, not status: a failed background refetch keeps the composer and its pick. */}
          {projects.data === undefined && projects.isError ? (
            <LoadError error={projects.error} retry={projects.refetch} busy={projects.isFetching} />
          ) : me.data === undefined && me.isError ? (
            <LoadError error={me.error} retry={me.refetch} busy={me.isFetching} />
          ) : projects.data === undefined || me.data === undefined ? (
            <Skeleton className="h-36 w-full rounded-xl" aria-label={t("home.loadingProjects")} />
          ) : (
            <Composer
              projects={projects.data}
              standaloneAllowed={me.data.standaloneAllowed === true}
            />
          )}
          <div className="flex justify-center">
            <ResidencyNote value={me.data?.residency?.value} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** What home needs could not load: say so, with a retry instead of a reload. */
function LoadError(props: { error: Error; retry: () => unknown; busy: boolean }) {
  const { t } = useTranslation();
  return (
    <Notice
      tone="danger"
      action={
        <Button size="sm" variant="outline" disabled={props.busy} onClick={() => props.retry()}>
          {t("app.retry")}
        </Button>
      }
    >
      {props.error.message}
    </Notice>
  );
}

/** The select's value for "No project": a standalone conversation. */
const NO_PROJECT = "";

function Composer(props: { projects: readonly ProjectView[]; standaloneAllowed: boolean }) {
  const { projects, standaloneAllowed } = props;
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const cache = useChatCache();
  const refresh = useRefreshConversations();
  const { draft, state } = useAttachmentDraft("new:home");
  const text = state.text;
  const setText = (text: string) => draft.edit({ text });
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useUnsavedWork(text.trim() !== "", text);
  // Only projects the user may ask in: a frozen project takes no new conversation.
  const askable = projects.filter((p) => !p.frozenAt);
  // The user's pick, else "No project" (when allowed), else the last project used; always
  // reconciled with the current list, so a project frozen or removed meanwhile is never the
  // target and a new one becomes usable.
  const [picked, setProjectId] = useState<string | null>(null);
  const preferred = picked ?? (standaloneAllowed ? NO_PROJECT : lastProject.read());
  const projectId =
    standaloneAllowed && preferred === NO_PROJECT
      ? NO_PROJECT
      : (askable.find((p) => p.id === preferred)?.id ?? askable[0]?.id ?? NO_PROJECT);
  const none = askable.length === 0 && !standaloneAllowed;
  const canSend =
    !none &&
    !starting &&
    state.files.length === 0 &&
    text.trim() !== "" &&
    (projectId !== NO_PROJECT || standaloneAllowed);
  // The effort picker follows the target: each project, or standalone, has its own policy.
  const choice = useEffortChoice(
    useModelOptions(none ? null : projectId === NO_PROJECT ? { standalone: true } : { projectId })
      .data,
  );
  const resetChoice = choice.pick;
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new target starts from its default
  useEffect(() => resetChoice(null), [projectId]);

  const attachments = useAttachmentStart({
    source: draft,
    csrfToken,
    options: choice.turnOptions,
    create: () =>
      projectId === NO_PROJECT
        ? api.api.conversations.$post({}, { headers: { [CSRF_HEADER]: csrfToken } }).then(ok)
        : api.api.projects[":projectId"].conversations
            .$post({ param: { projectId } }, { headers: { [CSRF_HEADER]: csrfToken } })
            .then(ok),
    started: (created) => {
      refresh();
      navigate(conversationPath(created));
    },
  });
  const send = async () => {
    if (!canSend || attachments.starting) {
      return;
    }
    // The session ended: keep the text where it is and point at the way to sign in again.
    if (blockedBySessionEnd()) {
      return;
    }
    const question = text.trim();
    if (projectId !== NO_PROJECT) {
      lastProject.write(projectId);
      navigate(projectConversationPath(projectId, "new"), {
        state: handOffQuestion(question, choice.turnOptions()),
      });
      setText("");
      return;
    }
    setStarting(true);
    // The question is in flight until the chat holds it, even if this screen is left meanwhile.
    const release = holdReload();
    setError(null);
    try {
      const created = await ok(
        await api.api.conversations.$post({}, { headers: { [CSRF_HEADER]: csrfToken } }),
      );
      // Sent from this handler, exactly once; the conversation view picks the chat up.
      const chat = createChat(created.id, csrfToken, [], () => refresh(created.id));
      const options = choice.turnOptions();
      void chat.sendMessage({ text: question }, options ? { body: { options } } : undefined);
      cache.keep(created.id, chat, true);
      setText("");
      refresh();
      navigate(conversationPath(created));
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === "standalone_disabled"
          ? t("home.standaloneDisabled")
          : e instanceof Error
            ? e.message
            : t("errors.unexpected"),
      );
    } finally {
      // After the route change's own attempt: by then the kept chat streams and holds.
      setTimeout(release, 0);
      setStarting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <ComposerFrame
        onFiles={none || starting ? undefined : attachments.select}
        describedBy={none ? `${id}-none` : undefined}
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <AttachmentTray list={undefined} draft={draft} state={state} />
        <ComposerField
          value={text}
          disabled={none}
          onChange={setText}
          onSend={() => void send()}
          label={t("home.title")}
          placeholder={t("home.ask")}
          describedBy={`${id}-hint`}
        />
        <ComposerFooter
          disabled={!canSend || attachments.starting}
          label={t("home.send")}
          leading={
            <>
              <AttachmentPicker
                onFiles={starting ? () => {} : attachments.select}
                disabled={none || starting}
              />
              {none ? (
                <span />
              ) : (
                <div className="flex min-w-0 items-center gap-2">
                  <label htmlFor={`${id}-project`} className="sr-only">
                    {t("home.project")}
                  </label>
                  <div className="relative min-w-0">
                    {projectId === NO_PROJECT ? (
                      <MessageCircle
                        aria-hidden="true"
                        className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-fg-muted"
                        strokeWidth={1.75}
                      />
                    ) : (
                      <FolderClosed
                        aria-hidden="true"
                        className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-fg-muted"
                        strokeWidth={1.75}
                      />
                    )}
                    <select
                      id={`${id}-project`}
                      value={projectId}
                      disabled={attachments.starting || state.files.length > 0}
                      onChange={(e) => setProjectId(e.currentTarget.value)}
                      className="h-8 w-full max-w-[18rem] min-w-0 appearance-none truncate rounded-full border border-transparent bg-transparent pr-7 pl-7.5 text-[13px] text-fg-secondary outline-none transition-colors duration-(--duration-fast) hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                    >
                      {standaloneAllowed ? (
                        <option value={NO_PROJECT}>{t("home.noProjectOption")}</option>
                      ) : null}
                      {askable.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      aria-hidden="true"
                      className="pointer-events-none absolute top-1/2 right-2 size-3.5 -translate-y-1/2 text-fg-muted"
                      strokeWidth={1.75}
                    />
                  </div>
                </div>
              )}
            </>
          }
          picker={
            choice.view ? (
              <EffortPicker
                view={choice.view}
                onPick={choice.pick}
                disabled={starting || attachments.starting}
              />
            ) : null
          }
        />
        {state.files.length > 0 ? (
          // In flight, a quiet status; the failure guidance only once a creation failed.
          <p className="px-5 pb-3 text-xs text-fg-secondary">
            {attachments.starting ? t("attachments.opening") : t("attachments.creationPending")}
          </p>
        ) : null}
      </ComposerFrame>
      {attachments.error && state.files.length > 0 ? (
        <Notice tone="danger">
          {attachments.error}
          <Button variant="ghost" onClick={() => attachments.select([])}>
            {t("attachments.retry")}
          </Button>
        </Notice>
      ) : null}

      <p id={`${id}-hint`} className="sr-only">
        {t("home.sendHint")}
      </p>
      {none ? (
        <div id={`${id}-none`}>
          <Notice
            title={t("home.noProjectTitle")}
            action={
              <Button size="sm" variant="outline" onClick={() => navigate(`${PROJECTS_PATH}?new`)}>
                <FolderPlus aria-hidden="true" strokeWidth={1.75} />
                {t("home.createProject")}
              </Button>
            }
          >
            {t("home.noProject")}
          </Notice>
        </div>
      ) : (
        <p className="text-center text-xs text-fg-muted">
          {projectId === NO_PROJECT ? t("home.standaloneHint") : t("home.projectHint")}
        </p>
      )}
      {error ? <Notice tone="danger">{error}</Notice> : null}
    </div>
  );
}
